<script>
	/* eslint-disable svelte/no-at-html-tags */
	// NPM Modules
	import dayjs from 'dayjs';
	import collect from 'collect.js';
	import Link from 'svelte-navaid/Link.svelte';
	import { createEventDispatcher, tick } from 'svelte';
	// Common
	import {
		getBetOddsForDisplay,
		getBetWinAmountForDisplay,
		getBetNetWinAmountForDisplay,
		isSportLeg,
		isBonusBet,
		isBoostedBet,
		isSportBet,
		isSGMBet,
		isExoticBet,
		isSRMBet,
		isMultiBet,
		isBoxedBet,
		isLiveBet,
		isCashedOutBet,
		isSGMLeg,
		isVirtualLeg,
		isRacingSinglesBet,
		hasPromotionOffer,
		isSportSinglesBet
	} from '@/Common/Bet.js';
	// Constants
	import { BetStatus } from '@/Constants/Bet';
	// Partials
	import StatusAdjustment from '@/Partials/Bets/_StatusAdjustment.svelte';
	import BetExtraInfo from '@/Partials/Bets/_BetExtraInfo.svelte';
	import ResultBet from '@/Partials/Bets/_ResultBet.svelte';
	import SGMBetDisplay from '@/Partials/Bets/_SGMBetDisplay.svelte';
	import SportBetDisplay from '@/Partials/Bets/_SportBetDisplay.svelte';
	import LegOddsDisplay from '@/Partials/Bets/_LegOddsDisplay.svelte';
	import RacingBetDisplay from '@/Partials/Bets/_RacingBetDisplay.svelte';
	import MultiLegStatusDisplay from '@/Partials/Bets/_MultiLegStatusDisplay.svelte';
	import SRMBetDisplay from '@/Partials/Bets/_SRMBetDisplay.svelte';
	import ExoticsBetDisplay from '@/Partials/Bets/_ExoticsBetDisplay.svelte';
	// Components
	import Icon from '@/Components/Icon.svelte';
	import FontIcon from '@/Components/FontIcon.svelte';
	// Services
	import { getIconForSelection } from '@/Services/Helpers.js';
	import { CurrencyFormat, routeForMeetingRace, routeForSportEvent } from '@/Services/Helpers';
	// Stores
	import ACLStore from '@/Stores/ACLStore.js';

	export let bet;
	export let index;
	export let manualResult = undefined;

	const dispatch = createEventDispatcher();

	// variables
	let showBetInfo = false;
	let showBetLegResult = false;

	async function openBetInfo() {
		showBetInfo = !showBetInfo;
		await tick();
		window.scrollTo(0, 0);
	}

	// GET PRIMARY LEG SELECTION
	$: selection = bet && bet.legs.data.first().leg_selections.data.first();

	// MULTI BETS
	let multiLegs = collect([]);
	$: bet && isMultiBet(bet) && setupMultiBet();

	function setupMultiBet() {
		let legs = collect([]);
		bet.legs.data.each((leg) => {
			let selection = leg.leg_selections.data.first();
			let legData = {
				id: leg.id,
				selection_id: selection.id,
				number: selection.number,
				name: selection.name,
				sport: selection.sport,
				sub_sport: selection.sub_sport,
				event: (selection.competition && selection.competition.name) || '-',
				sport_event: (selection.event && selection.event.name) || '-',
				event_number: (selection.event && selection.event.number) || '-',
				route_event: selection.event,
				route_meeting: selection.competition || null,
				bet_type: leg.bet_type,
				is_each_way: leg.is_each_way,
				is_virtual: leg.is_virtual,
				is_boxed: leg.is_boxed,
				status: leg.status,
				product: leg.product,
				fixed_price: leg.fixed_price,
				leg_number: leg.leg_number,
				selection_status: selection.status
			};

			if (isSGMLeg(leg)) {
				legData['selection'] = selection;
			}

			if (selection.market) {
				legData['market'] = selection.market;
			}
			legs.push(legData);
		});
		multiLegs = legs;
	}

	async function openBetLegResult() {
		showBetLegResult = true;
		await tick();
		window.scrollTo(0, 0);
	}
</script>

<div class="table-row text-sm text-center hover:bg-gray-200 {index % 2 ? 'bg-light' : 'bg-gray-100'}">
	<!-- COMMON -->
	<div class="table-cell px-4 py-2 text-xs border-b align-middle">
		<div class="font-semibold">
			<Link class="hover:underline" href="/bet-ticker?ticket={bet.ticket}" target="_blank" title="Open in bet ticker">
				<span class="flex flex-row justify-center items-center">
					#{bet.ticket}
					<Icon name="open" classes="w-3 h-3 ml-px" />
				</span>
			</Link>
		</div>
		<div>{dayjs(bet.created_at).format('ddd D MMM YYYY')}</div>
		<div class="text-gray-600">
			{dayjs(bet.created_at).format('HH:mm:ss Z')}
		</div>
	</div>
	<div class="table-cell px-2 py-2 border-b align-middle">
		<div class="flex flex-row space-x-1 items-center justify-center">
			<FontIcon icon={getIconForSelection(selection)} className="mr-1" />
			{selection.type.name}
			<span class="px-2 py-1 text-xs bg-gray-300 rounded-md">
				{`${(isBoxedBet(bet) ? 'Boxed ' : '') + bet.bet_type.name}`}
			</span>
			{#if isLiveBet(bet)}
				<div class="flex flex-row ml-1 items-center text-center uppercase rounded text-sm">
					<FontIcon icon={'live-event'} className="text-secondary rounded-sm ml-0.5" />
				</div>
			{/if}
		</div>
	</div>
	<div class="table-cell px-4 py-2 text-xs text-left border-b align-middle">
		{#if isSportBet(bet)}
			<Link href={routeForSportEvent(selection.event)} class="focus:outline-none hover:underline" target="_blank">
				<div>{(selection.competition && selection.competition.name) || '-'}</div>
				<div>{(selection.event && selection.event.name) || '-'}</div>
			</Link>
		{:else if isMultiBet(bet)}
			{#each multiLegs && multiLegs.all() as leg}
				{#if !isVirtualLeg(leg)}
					{#if isSportLeg(leg) || isSGMLeg(leg)}
						<div class="mb-0.5">
							<Link
								href={routeForSportEvent(leg.route_event)}
								class="focus:outline-none hover:underline"
								target="_blank"
							>
								L{leg.leg_number}: {leg.sport_event}
							</Link>
						</div>
					{:else}
						<div class="mb-0.5">
							<Link
								href={routeForMeetingRace(leg.route_meeting, leg.route_event)}
								class="focus:outline-none hover:underline"
								target="_blank"
							>
								L{leg.leg_number}:{leg.event} - Race {leg.event_number}
							</Link>
						</div>
					{/if}
				{/if}
			{/each}
		{:else}
			<Link
				href={routeForMeetingRace(selection.competition, selection.event)}
				class="focus:outline-none hover:underline"
				target="_blank"
			>
				{selection.competition.name} - Race {selection.event.number}
			</Link>
		{/if}
	</div>
	<div class="table-cell px-4 py-2 text-xs text-left border-b align-middle group">
		<div class="flex flex-row w-full justify-between">
			<div class="flex flex-col text-xs">
				<div class="flex flex-row items-start space-x-1.5 mb-0.5">
					<div class="font-semibold">
						{(isBoxedBet(bet) ? 'Boxed ' : '') + bet.bet_type.name}
					</div>
					{#if isLiveBet(bet)}
						<div class="flex flex-row text-center uppercase rounded text-sm" style="font-size: .8rem;">
							<FontIcon icon={'live-event'} className="text-secondary rounded-sm" />
						</div>
					{/if}
				</div>
				<div class="flex flex-row">
					{#if isSportSinglesBet(bet)}
						<SportBetDisplay {bet} {selection} {manualResult} />
					{:else if isSGMBet(bet)}
						<SGMBetDisplay {selection} {manualResult} />
					{:else if isRacingSinglesBet(bet)}
						<RacingBetDisplay {bet} {selection} />
						<LegOddsDisplay {bet} selection={bet.legs.data.first()} showGuided={true} />
					{:else if isExoticBet(bet)}
						<ExoticsBetDisplay {bet} fancyDisplay={false} />
					{:else if isSRMBet(bet)}
						<SRMBetDisplay {bet} />
					{:else if isMultiBet(bet)}
						<div class="flex flex-col">
							{#each multiLegs && multiLegs.all() as leg (leg.id)}
								{#if !isVirtualLeg(leg)}
									<div class="flex flex-row items-center space-x-1 mb-0.5">
										<MultiLegStatusDisplay {leg} {multiLegs} />
										<div class="flex flex-row">
											{#if isSportLeg(leg)}
												<SportBetDisplay {bet} selection={leg} {manualResult} inMulti={true} />
											{:else if isSGMLeg(leg)}
												<SGMBetDisplay selection={leg} {manualResult} inMulti={true} />
											{:else}
												<RacingBetDisplay {bet} selection={leg} inMulti={true} />
											{/if}
										</div>
										<LegOddsDisplay {bet} selection={leg} showGuided={true} inMulti={true} />
									</div>
								{/if}
							{/each}
						</div>
					{/if}
				</div>
			</div>
			<div class="w-4 my-auto">
				{#if $ACLStore && ACLStore.granted('bets.update') && bet.status.id === BetStatus.Active && ((isSportBet(bet) && !isSGMBet(bet)) || isMultiBet(bet))}
					<button
						on:click={() => openBetLegResult()}
						class="flex items-center justify-center w-full space-x-1 rounded-md focus:outline-none btn p-0"
					>
						<Icon name="pencil" classes="w-4 h-4 hidden group-hover:block group-hover:text-orange-500" />
					</button>
				{/if}
			</div>
		</div>
	</div>
	<div class="table-cell px-4 border-b align-middle">
		<div class="flex flex-col justify-center">
			<div class="flex flex-row justify-center items-center">
				{#if isBonusBet(bet)}
					<FontIcon icon={'gift'} className="text-white bg-gray-500 p-1 rounded-sm text-tiny mr-1" />
				{/if}
				<span>{CurrencyFormat(bet.bet_amount)}</span>
				<div class="mx-0.5 mb-0.5 inline-block align-text-top">@</div>
				{getBetOddsForDisplay(bet)}
				{#if isBoostedBet(bet)}
					<FontIcon icon={'boost-up'} className="ml-1 text-primary" />
				{/if}
				{#if isCashedOutBet(bet)}
					<FontIcon icon="cashout" className="ml-1 text-primary" />
				{/if}
				{#if hasPromotionOffer(bet)}
					<FontIcon icon="bonus-back" className="ml-1 text-primary" />
				{/if}
			</div>
			{#if bet.is_partially_accepted}
				<div class="text-sm text-danger">
					(Partialled from: {CurrencyFormat(bet.requested_bet_amount)})
				</div>
			{/if}
		</div>
	</div>
	<div class="table-cell px-4 py-2 border-b align-middle">
		<StatusAdjustment {bet} />
	</div>
	<div
		class="table-cell px-4 py-2 border-b align-middle"
		title={bet.adjustment_amount ? `Inc. ${CurrencyFormat(bet.adjustment_amount)} adjustment` : ''}
	>
		{getBetWinAmountForDisplay(bet)}
	</div>
	<div class="table-cell px-4 py-2 border-b align-middle">
		{getBetNetWinAmountForDisplay(bet)}
	</div>
	<div class="table-cell px-4 py-2 border-b align-middle">
		<button on:click={openBetInfo} class="outline-none focus:outline-none">
			<Icon name="info" classes="w-4 h-4 text-blue-500" />
		</button>
	</div>
</div>

{#if showBetInfo}
	<BetExtraInfo bind:open={showBetInfo} bind:bet />
{/if}
{#if showBetLegResult}
	<ResultBet bind:open={showBetLegResult} bind:bet on:updated={() => dispatch('updated')} />
{/if}
